import * as XLSX from "xlsx";
import {
  getColumnWidths,
  getFeedbacksSheet,
  calculateStatistics,
  createInfoSheet,
} from "./common";
import * as _ from "lodash";
import {
  TManualChoiceSession,
  TManualChoiceInstances,
  TManualChoiceInstance,
} from "types/manualChoice";

export const exportManualChoiceResults = (
  session: TManualChoiceSession,
  instances: TManualChoiceInstances
) => {
  const excelFile = XLSX.utils.book_new();

  // Créer l'onglet d'informations générales
  const infoSheet = createInfoSheet(
    session.metadata.name,
    "Manual Choice",
    session.metadata.context
  );
  XLSX.utils.book_append_sheet(excelFile, infoSheet, "Information");

  const feedbacksSheet = getFeedbacksSheet(session, instances);
  const { formatedResults } = getStatsData(session, instances);

  // Grouper les résultats par séquence
  const resultsBySequence = _.groupBy(formatedResults, "sequence");

  // Créer un onglet pour chaque séquence
  Object.keys(resultsBySequence).forEach((sequenceContext, index) => {
    const sequenceResults = resultsBySequence[sequenceContext];
    const resultsSheet = createResultsSheet(sequenceResults);

    if (resultsSheet) {
      XLSX.utils.book_append_sheet(
        excelFile,
        resultsSheet,
        `Sequence ${index + 1}`
      );
    }

    // Créer un onglet de statistiques pour cette séquence
    const sequenceStats = getSequenceStatistics(
      session,
      instances,
      sequenceContext
    );
    const sequenceStatsSheet = createSequenceStatisticsSheetWithAttributes(
      sequenceStats,
      session,
      sequenceContext,
      ["Ressource", "Average", "Median", "Standard deviation"]
    );

    if (sequenceStatsSheet) {
      XLSX.utils.book_append_sheet(
        excelFile,
        sequenceStatsSheet,
        `Stats Sequence ${index + 1}`
      );
    }
  });

  if (feedbacksSheet) {
    XLSX.utils.book_append_sheet(excelFile, feedbacksSheet, "Feedbacks");
  }

  XLSX.writeFile(excelFile, `export_session_${session.id}.xlsx`);
};

const createResultsSheet = (resutsPerSequences: any[]) => {
  if (!resutsPerSequences || resutsPerSequences.length === 0) {
    return null;
  }

  const excelRows = [];
  const allHeaders = [...Object.keys(resutsPerSequences[0] || {})];
  // Exclure sequence de l'affichage
  const headers = allHeaders.filter((header) => header !== "sequence");
  excelRows.push(headers);

  resutsPerSequences.forEach((row: any) => {
    const rowData = headers.map((header) => row[header] ?? "");
    excelRows.push(rowData);
  });

  const resultsSheet = XLSX.utils.aoa_to_sheet(excelRows);
  const colWidths = getColumnWidths(resutsPerSequences);
  resultsSheet["!cols"] = colWidths;

  return resultsSheet;
};

const getStatsData = (
  session: TManualChoiceSession,
  instances: TManualChoiceInstances
) => {
  const { data, metadata } = session;
  const { sequences = [] } = data;
  const { personalQuestions = [] } = metadata;
  const results = {};

  // Merge all instance results
  instances.map((instance: TManualChoiceInstance) =>
    _.merge(results, instance.results)
  );

  const formatedResults: any[] = [];

  _.keys(results).map((sequenceId) => {
    // @ts-ignore
    const sequenceResult = results[sequenceId];
    const participantsVote = _.omit(sequenceResult, "_fakeId");

    // Find the sequence to get its context/question
    const sequence = sequences.find((s) => s.id === sequenceId);

    // For each participant vote
    for (let participantId in participantsVote) {
      const participant = instances
        ?.find((instance: TManualChoiceInstance) =>
          instance.participants?.find((p) => p.id === participantId)
        )
        ?.participants?.find((p) => p.id === participantId);

      const participantName =
        participant?.infos &&
        typeof participant.infos === "object" &&
        "name" in participant.infos
          ? (participant.infos as any).name
          : participantId;

      const vote = participantsVote[participantId]; // This is the selected resource ID

      // Find the selected resource name
      const selectedResource = sequence?.resources.find((r) => r.id === vote);
      const selectedResourceName = selectedResource?.name || "No selection";

      const sequenceFormatedResult: any = {
        sequence: sequenceId || "unknown",
        Participant: participantName,
        "Selected Resource": selectedResourceName,
      };

      // Add personal questions columns
      personalQuestions.forEach((question) => {
        const instance = instances.find((inst: TManualChoiceInstance) =>
          inst.participants?.find((p) => p.id === participantId)
        );

        const answer = instance?.personalQuestions?.[question.id] || "";
        sequenceFormatedResult[question.question] = answer;
      });

      formatedResults.push(sequenceFormatedResult);
    }

    return null;
  });

  return { formatedResults };
};

// Fonction pour calculer les statistiques d'une séquence spécifique
const getSequenceStatistics = (
  session: TManualChoiceSession,
  instances: TManualChoiceInstances,
  sequenceContext: string
) => {
  const { data } = session;
  const { sequences = [] } = data;
  const results = {};
  const sequenceStats: any[] = [];

  // Merge all instance results
  instances.map((instance: TManualChoiceInstance) =>
    _.merge(results, instance.results)
  );

  // Trouver la séquence correspondante à l'ID
  const sequence = sequences.find((s) => s.id === sequenceContext);
  if (!sequence) {
    return [];
  }

  // Initialiser les statistiques pour cette séquence
  sequence.resources.forEach((resource) => {
    sequenceStats.push({
      resourceId: resource.id,
      resourceName: resource.name,
      selections: [],
    });
  });

  // Récupérer les résultats pour cette séquence spécifique
  const sequenceId = sequence.id;
  // @ts-ignore
  const sequenceResult = results[sequenceId];
  if (!sequenceResult) {
    return [];
  }

  const participantsVote = _.omit(sequenceResult, "_fakeId");

  // Collecter les sélections pour cette séquence
  for (let participantId in participantsVote) {
    const vote = participantsVote[participantId]; // This is the selected resource ID

    sequenceStats.forEach((stat) => {
      if (stat.resourceId === vote) {
        stat.selections.push(1); // Ressource sélectionnée
      } else {
        stat.selections.push(0); // Ressource non sélectionnée
      }
    });
  }

  // Calculer les statistiques finales pour cette séquence
  const finalStats = sequenceStats.map((stat) => {
    const stats = calculateStatistics(stat.selections);
    return {
      resourceName: stat.resourceName,
      ...stats,
    };
  });

  return finalStats;
};

// Fonction pour créer un onglet de statistiques de séquence avec attributs
const createSequenceStatisticsSheetWithAttributes = (
  sequenceStats: any[],
  session: TManualChoiceSession,
  sequenceContext: string,
  baseHeaders: string[]
) => {
  if (!sequenceStats || sequenceStats.length === 0) {
    return null;
  }

  const excelRows = [];

  // En-têtes de base - récupérer les attributs depuis la séquence spécifique
  const currentSequence = session.data.sequences.find(
    (s) => s.id === sequenceContext
  );
  const attributeHeaders = Array.isArray(currentSequence?.attributes)
    ? currentSequence.attributes.map((attr) => `${attr.name} - ${attr.unit}`)
    : [];
  const allHeaders = [...baseHeaders, ...attributeHeaders];

  excelRows.push(allHeaders);

  // Données
  sequenceStats.forEach((stat) => {
    const baseData = baseHeaders.map((header) => {
      switch (header) {
        case "Average":
          return stat.average;
        case "Median":
          return stat.median;
        case "Standard deviation":
          return stat.standardDeviation;
        default:
          return stat.resourceName || "";
      }
    });

    // Ajouter les valeurs des attributs
    const attributeValues = Array.isArray(currentSequence?.attributes)
      ? currentSequence.attributes.map((attr, index) => {
          const resource = currentSequence.resources.find(
            (r) => r.name === stat.resourceName
          );
          return resource?.attributes?.[index]?.quantity || "";
        })
      : [];

    const allData = [...baseData, ...attributeValues];
    excelRows.push(allData);
  });

  const statisticsSheet = XLSX.utils.aoa_to_sheet(excelRows);

  // Ajuster la largeur des colonnes
  const colWidths = allHeaders.map(() => ({ width: 20 }));
  statisticsSheet["!cols"] = colWidths;

  return statisticsSheet;
};
